import env from './env';

const handleUnauthorizedReq = () => {
  window.location = `${env.loginUrl}?origin=${window.location.href}`;
};

const handleInvalidBoxID = () => {
  console.error('Invalid BoxID');
  window.location = '/';
};

// makes an API call and returns a JSON object
const sendRequest = (
  path,
  method = 'GET',
  body,
  additionalHeaders = {},
  json = true,
  cache = 'default',
) => {
  const defaultHeaders = {
    'FFF-Auth': 'V1.1',
    Origin: env.originURL,
    'Content-Type': 'application/json; charset=utf-8',
  };
  return fetch(path, {
    credentials: 'include',
    headers: { ...defaultHeaders, ...additionalHeaders },
    data: {
      activatedOn: true,
    },
    body: method === 'POST' || method === 'PUT' || method === 'PATCH' ? JSON.stringify(body) : body,
    mode: 'cors',
    method,
    cache,
  })
    .then((response) => {
      const code = response.code || response.status;
      const message = response.message ? response.message : '';
      if (code === 401) return handleUnauthorizedReq();
      if (code === 404 && message === 'Invalid BoxID') return handleInvalidBoxID();
      return response;
    })
    .then((response) => json && response.json())
    .catch((err) => {
      console.error('Fetch Failed: ', err);
      return { error: err, success: false };
    });
};

export const checkAuth = () => sendRequest(env.loginVerify);

export const getUserData = () => sendRequest(`${env.user}/self`);

export const getUserAccountCode = () => sendRequest(`${env.user}/self/status`);

export const getBoxData = () => sendRequest(`${env.box}/self`, 'GET', null, {}, true, 'no-store');

export const getUserFirstName = (boxId = false) => {
  if (boxId) {
    return sendRequest(`${env.user}/firstname/boxId/${boxId}`, 'GET', null, {}, true, 'no-store');
  }
  return false;
};

export const getUserAvatar = (boxId = false) => {
  if (boxId) {
    return sendRequest(
      `${env.hermes}/users/avatar/boxId/${boxId}`,
      'GET',
      null,
      {},
      true,
      'no-store',
    );
  }
  return false;
};

export const getSkipInfo = () => sendRequest(env.accounts);

export const request = (method, url, headers = {}, body = null) => {
  let options = { method, headers: new Headers(headers) };
  if (body) options = Object.assign({}, options, { body: JSON.stringify(body) });
  const req = new Request(url, options);
  return req;
};

export const saveRevealState = (sku, value) =>
  sendRequest(`${env.box}/sku/${sku}/reveal`, 'PUT', {
    reveal: value,
  });

export const getSpecData = (id) => sendRequest(`${env.erosURL}${id}.json`);

/**
 * Returns a Request object that we can use to make API requests
 * @param {string} method - The HTTP method to use
 * @param {string} path - The path to the API endpoint
 * @param {object} body - The body of the request
 * @returns {Request} - The Request object
 */
export function apiRequest(method = 'GET', path, body) {
  let headers = {
    'FFF-Auth': 'V1.1',
  };

  if (method === 'POST' || method === 'PUT' || method === 'DELETE' || method === 'PATCH') {
    headers = {
      ...headers,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  const options = {
    method,
    credentials: 'include',
    headers: new Headers(headers),
  };

  if ((method === 'PUT' || method === 'POST' || method === 'DELETE' || method === 'PATCH') && body)
    options.body = JSON.stringify(body);
  return new Request(path, options);
}

export const getUserBoxReview = () =>
  sendRequest(`${env.questionnaire}/ratings/self`, 'GET', null, {}, true, 'no-store');

export const getSaleProducts = (saleId) =>
  sendRequest(
    `${env.storefront}/api/1/sales/${saleId}/products?countryIsoCode=US&limit=999&offset=0&orderBy=best_selling&preview=false`,
    'POST',
    null,
    {},
    true,
    'no-store',
  );

export const getCustomizeSaleInfo = (customizeSaleId, countryIsoCode = 'US') =>
  sendRequest(
    `${env.storefront}/api/1/sales/${customizeSaleId}/customize-sale-info?countryIsoCode=${countryIsoCode}&isPublic=false&preview=false`,
    'GET',
    null,
    {},
    true,
    'no-store',
  );

export const processBoxSubscription = (cartId, saleUuid, data) =>
  sendRequest(
    `${env.ace}api/1/carts/${cartId}/update?checkoutCustomizeSaleUuid=${saleUuid}`,
    'PATCH',
    data,
    {},
    true,
    'no-store',
  );

/**
 * Adds cart items to the user's CUSTOMER cart (the active shop cart).
 * @param {string} cartUuid - The CUSTOMER cart uuid.
 * @param {Array} cartItems - Cart items in maui's `CustomizeCartItemPayload` shape.
 * @returns {Promise} - The updated cart response.
 */
export const updateUserCart = (cartUuid, cartItems) =>
  sendRequest(
    `${env.ace}api/1/carts/${cartUuid}/update`,
    'PATCH',
    cartItems,
    {},
    true,
    'no-store',
  );

/**
 * Get user cart data
 * @returns {Promise} - The cart data response. Each cart is normalized so
 *   `prepaidSubscriptionGroups` is always an array, even if the backend omits it.
 */
export const getUserCartData = async () => {
  const queryParams = new URLSearchParams({
    checkedOut: false,
    timerId: 0,
    sourceType: 'CUSTOMIZE',
  });

  const url = `${env.ace}api/1/carts?${queryParams.toString()}`;

  const response = await sendRequest(url, 'GET', null, {}, true, 'no-store');

  if (Array.isArray(response)) {
    return response.map((cart) => ({
      ...cart,
      prepaidSubscriptionGroups: cart?.prepaidSubscriptionGroups ?? [],
    }));
  }

  return response;
};
